import React, { useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { BASE_URL, API_ENDPOINTS } from "../config/apiconfig";
import "../style/Login.css";
import { TokenManager } from "../utils/tokenManager";
import appPreloader from "../services/appPreloader";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [preloading, setPreloading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const LOGIN_API = `${BASE_URL}${API_ENDPOINTS.AUTH.LOGIN}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!username || !password) {
      setError("Username and password are required");
      toast.error("Username and password are required");
      return;
    }

    try {
      setLoading(true);

      const response = await axios.post(LOGIN_API, {
        username: username.trim().toLowerCase(),
        password,
      });

      console.log('Login response:', response.data);

      // Handle different response structures
      const token = response.data?.token || response.data?.data?.token || response.data;

      if (!token) {
        throw new Error('No token received from server');
      }

      TokenManager.setToken(token);

      // Store subscription plan from response
      const plan = response.data?.subscriptionPlan || response.data?.data?.subscriptionPlan;
      if (plan) {
        localStorage.setItem('user_plan', plan);
      }

      toast.success("Login successful");

      // Preload essential data
      setPreloading(true);
      try {
        await appPreloader.preloadAll();
        console.log('Data preloaded successfully');
      } catch (error) {
        console.error('Error preloading data:', error);
        // Continue to dashboard even if preloading fails
      } finally {
        setPreloading(false);
      }

      setTimeout(() => {
        window.location.href = "/dashboard";
      }, 300);
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response data:', error.response?.data);

      // Better error handling
      let errorMessage = 'Login failed';
      if (error.response) {
        // Server responded with error status
        // Try different possible response structures
        errorMessage = error.response.data?.message ||
                         error.response.data?.error ||
                         error.response.data ||
                         'Invalid credentials';
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection.';
      } else {
        // Other error
        errorMessage = error.message || 'Login failed';
      }

      // Ensure errorMessage is a string
      if (typeof errorMessage !== 'string') {
        errorMessage = 'Invalid credentials';
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">

      {/* Background Overlay */}
      <div className="login-overlay"></div>

      {/* Content */}
      <div className="login-content">

        {/* LEFT TEXT */}
        <div className="login-left">
          <h1>Zerlak One</h1>
          <p>
            Track Cylinders Smarter - Reduce up to 92% manual work with automated tracking,
            real-time insights, and streamlined operations.
          </p>
        </div>

        {/* RIGHT FORM */}
        <div className="login-card">

          <h2>Sign In</h2>

          <form onSubmit={handleSubmit}>

            <div className="input-group">
              <input
                type="text"
                placeholder="Username"
                value={username}
                maxLength={50}
                onChange={(e) => setUsername(e.target.value.trim())}
              />
            </div>

            <div className="input-group password">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                maxLength={100}
                onChange={(e) => setPassword(e.target.value)}
              />
              <span onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? "Hide password" : "Show password"}>
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46A11.804 11.804 0 001 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2.53zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                  </svg>
                )}
              </span>
            </div>

            {error && (
              <div className="login-error">
                <div className="login-error-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="login-error-text">{error}</div>
              </div>
            )}

            <div className="login-options">
              <span>Forgot Password?</span>
            </div>

            <button type="submit" disabled={loading || preloading}>
              {preloading ? "Loading application..." : loading ? "Signing in..." : "Sign in"}
            </button>

          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;